// =============================================================================
// ui/state/journal.tsx — the one place loaded data lives, and the ONLY thing
// that hands it to a selector.
// -----------------------------------------------------------------------------
// Phase 4 opens here rather than on a tab, because every tab reads from this and
// the mistake it prevents is not recoverable by fixing a component later.
//
// ── 🎯 WHAT THIS FILE IS ACTUALLY FOR (D47) ──
//
// It holds `Population<MetricsRow>`, never `MetricsRow[]`.
//
// The boot read is BOUNDED — 45 days, not the whole book. Window-scoped numbers
// (win rate on a date range, P&L for a month) are provably identical under that
// partial read. Population-scoped ones are NOT: account balance, drawdown
// buffer and consistency read from every trade that ever happened, and computed
// over 45 days they come out **wrong in the OPTIMISTIC direction** — a bigger
// balance, more drawdown room than the trader actually has — and they are
// self-consistent while wrong, so nothing looks broken.
//
// `selectors/population.ts` makes those uncomputable without a `WholeBook`, and
// that type only exists once a receipt earns it. Storing a bare array here would
// throw the guarantee away at the first hop: every component downstream would
// receive something a Class-L function happily accepts.
//
// So the array is never unwrapped in this file. Consumers get the Population and
// pass it on. `rowsForWindow()` is how a Class-W caller gets rows — by asking
// for a window the read demonstrably covers, not by reaching inside.
//
// ── LAYERING ──
// ui/ may not import domain/ AT ALL (scripts/check-layering.mjs). Everything
// rendered comes through selectors/. This file imports io/ to LOAD and
// selectors/ to interpret — it computes nothing.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { bootWindowStart, loadTradeMetrics, loadTradeMetricsBackfill } from '../../io/sync/load';
import { mergeReceipts, type ReadReceipt } from '../../io/sync/receipt';
import { populationFrom, type MetricsRow, type Population } from '../../selectors/population';
import { reportFailure } from '../../io/diagnostics/errorLog';

// ── The shape ────────────────────────────────────────────────────────────────

export type LoadPhase =
  /** Nothing yet. No number of any class is safe to render. */
  | 'idle'
  /** The bounded boot read is in flight. */
  | 'booting'
  /** Boot done: the recent window is real. Class-L numbers are still BLOCKED. */
  | 'windowed'
  /** Backfill in flight; the window stays usable throughout. */
  | 'backfilling'
  /** The whole book is loaded. Class-L numbers become computable. */
  | 'whole'
  /** Something failed. `error` says what; whatever loaded stays usable. */
  | 'failed';

export interface JournalState {
  phase: LoadPhase;
  /** ⚠ A Population, never an array. See the header. */
  trades: Population<MetricsRow> | null;
  receipt: ReadReceipt | null;
  error: string | null;
  /** Today, as YYYY-MM-DD, resolved ONCE per session — see below. */
  today: string;
  /** Start the backfill that turns a bounded read into a whole book. */
  loadWholeBook: () => void;
}

const JournalContext = createContext<JournalState | null>(null);

export function useJournal(): JournalState {
  const ctx = useContext(JournalContext);
  // A component rendering outside the provider would otherwise read `null` and
  // most likely render an empty journal — indistinguishable from a real one.
  if (!ctx) throw new Error('useJournal() outside <JournalProvider>');
  return ctx;
}

// ── The provider ─────────────────────────────────────────────────────────────

export interface JournalProviderProps {
  sb: SupabaseClient;
  children: ReactNode;
  /**
   * Injected for tests, and for the Deno aggregate's calling convention.
   *
   * ⚠ Resolved ONCE and held, never recomputed per render. `new Date()` at
   * render time means a session open across midnight silently changes the boot
   * window under a user who has not touched anything — every date-bucketed
   * number shifts by a day mid-session with no reload and no explanation.
   */
  today?: string;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

export function JournalProvider({ sb, children, today: todayProp }: JournalProviderProps) {
  const [today] = useState(() => todayProp ?? todayIso());
  const [phase, setPhase] = useState<LoadPhase>('idle');
  const [trades, setTrades] = useState<Population<MetricsRow> | null>(null);
  const [receipt, setReceipt] = useState<ReadReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The rows behind the Population, kept so a backfill can be concatenated
  // without unwrapping the Population itself (which would defeat the type).
  const rowsRef = useRef<MetricsRow[]>([]);
  // Guards a second backfill while one is running, and after one has completed.
  const backfilling = useRef(false);

  // ── Boot: the bounded read ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setPhase('booting');

    void (async () => {
      try {
        const since = bootWindowStart(today);
        const res = await loadTradeMetrics(sb, { since });
        if (cancelled) return;
        rowsRef.current = [...res.rows];
        setTrades(populationFrom(res.rows, res.receipt));
        setReceipt(res.receipt);
        setPhase('windowed');
      } catch (e) {
        if (cancelled) return;
        // ⚠ A failed read is NOT an empty journal. Leaving `trades` null keeps
        // every consumer on its "no data yet" path instead of rendering zeroes
        // that look like a real answer — the same distinction the old client
        // got wrong with entitlement, where a failed SELECT booted a paying
        // customer as Free.
        setError(reportFailure('journal', e));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, today]);

  // ── Backfill: what earns a WholeBook ───────────────────────────────────────
  const loadWholeBook = useCallback(() => {
    if (backfilling.current) return;
    if (!receipt || receipt.coversWholeBook) return;
    backfilling.current = true;
    setPhase('backfilling');

    void (async () => {
      try {
        const before = bootWindowStart(today);
        const res = await loadTradeMetricsBackfill(sb, before);
        // ⚠ mergeReceipts is the ONLY path to coversWholeBook. Constructing a
        // whole-book receipt by hand here would hand every Class-L number a
        // guarantee nothing verified.
        const merged = mergeReceipts(receipt, res.receipt);
        rowsRef.current = [...rowsRef.current, ...res.rows];
        setTrades(populationFrom(rowsRef.current, merged));
        setReceipt(merged);
        setPhase(merged.coversWholeBook ? 'whole' : 'windowed');
      } catch (e) {
        // The boot window is still valid, so this is NOT 'failed' — Class-W
        // numbers keep working and only the Class-L unlock is denied. Reporting
        // it as a total failure would blank a journal that is fine.
        setError(reportFailure('journal', e));
        setPhase('windowed');
      } finally {
        backfilling.current = false;
      }
    })();
  }, [sb, today, receipt]);

  const value = useMemo<JournalState>(
    () => ({ phase, trades, receipt, error, today, loadWholeBook }),
    [phase, trades, receipt, error, today, loadWholeBook],
  );

  return <JournalContext.Provider value={value}>{children}</JournalContext.Provider>;
}
